import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Checkbox,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle, ExpandMore, RadioButtonUnchecked } from '@mui/icons-material';
import {
  FRENZEL_CITATION,
  FRENZEL_PRACTICE_GUIDANCE,
  FRENZEL_SAFETY,
  FRENZEL_STEPS,
  MOUTHFILL_OUT_OF_SCOPE,
} from '../../data/frenzelSteps';

/**
 * Frenzel equalisation progression.
 *
 * Six sequential steps, each with a success criterion so "done" means something
 * specific rather than "I read it". Progress is stored on the apnea profile.
 *
 * Step 5 carries an `omitsSource` note because the printed workshop chains it onto
 * lung packing, which is prohibited throughout this app. The divergence is shown to
 * the user rather than quietly hidden.
 */

interface FrenzelTrackerProps {
  progress: Record<string, boolean>;
  saving?: boolean;
  onToggle: (step: number, done: boolean) => void;
}

const FrenzelTracker: React.FC<FrenzelTrackerProps> = ({ progress, saving = false, onToggle }) => {
  const completed = FRENZEL_STEPS.filter(s => progress[String(s.step)]).length;
  const pct = (completed / FRENZEL_STEPS.length) * 100;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="subtitle1">Frenzel equalisation</Typography>
        <Chip size="small" label={`${completed} of ${FRENZEL_STEPS.length} steps`} />
      </Stack>

      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, mb: 2 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {FRENZEL_PRACTICE_GUIDANCE}
      </Typography>

      {FRENZEL_STEPS.map(step => {
        const done = Boolean(progress[String(step.step)]);
        return (
          <Accordion
            key={step.step}
            disableGutters
            elevation={0}
            sx={{ '&:before': { display: 'none' }, borderTop: 1, borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
                <Checkbox
                  checked={done}
                  disabled={saving}
                  onClick={e => e.stopPropagation()}
                  onChange={e => onToggle(step.step, e.target.checked)}
                  icon={<RadioButtonUnchecked />}
                  checkedIcon={<CheckCircle color="success" />}
                  size="small"
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, textDecoration: done ? 'line-through' : 'none' }}
                >
                  {step.step}. {step.title}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pl: 5 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {step.goal}
              </Typography>
              <List dense disablePadding>
                {step.instructions.map((line, i) => (
                  <ListItem key={line} disableGutters sx={{ alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Typography variant="caption" color="text.secondary">{i + 1}.</Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={line}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
              <Alert severity="success" sx={{ mt: 1 }} icon={false}>
                <Typography variant="caption">
                  <strong>You have it when:</strong> {step.successCriteria}
                </Typography>
              </Alert>
              {step.caution && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="caption">{step.caution}</Typography>
                </Alert>
              )}
              {step.omitsSource && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    <strong>Note on this step:</strong> {step.omitsSource}
                  </Typography>
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Alert severity="warning" sx={{ mt: 2 }}>{FRENZEL_SAFETY}</Alert>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {MOUTHFILL_OUT_OF_SCOPE}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {FRENZEL_CITATION}
      </Typography>
    </Paper>
  );
};

export default FrenzelTracker;
